/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 * @author Tomáš Burian (burian@cadwork.cz)
 */

import IScene from "../scene_interface";
import {RenderDocument, Debug, Canvas} from "js_renderer";
import Settings from "./settings";
import IDocument from "../structural_elements/document_interface";
import {vec2} from "gl-matrix";
import {ESceneModules} from "../scene_enums";
import IAbstractModule from "./abstract_module_interface";

export type TSceneModuleType = string;

export interface IModuleData {
    [index: string]: any;
}

export interface IExternalObserver {
    getName(): string;
    onChange(aData: any);
}

export interface IModuleDependency {
    getClassType(): string;
}

export default
abstract class AbstractModule implements IAbstractModule {

    protected static _typeName: TSceneModuleType;
    protected static _eType: ESceneModules;

    protected sceneAPI: IScene;
    protected canvas: Canvas;
    protected sceneDocument: IDocument;
    protected renderDocument: RenderDocument;

    protected _defaultSettings: any;    // module default settings; updated when main model loaded from its model description
    protected _settings: any;           // module current settings
    protected _data: IModuleData;       // module data saved per model
    protected _type: TSceneModuleType;

    protected _settingsObservers: IExternalObserver[];  // external settings observers; eg. from application
    // protected _dataObservers: IExternalObserver[];   // external data observers; eg. from application
    protected _observers: AbstractModule[];             // internal scene api observers // when to register refs?
    protected _dependencies: TSceneModuleType[];        // other modules dependencies

    constructor(aRenderDocument: RenderDocument) {

        this.renderDocument = aRenderDocument;
        this.sceneAPI = aRenderDocument.getSceneAPI();
        this.canvas = this.sceneAPI.getCanvas();
        this.sceneDocument = this.sceneAPI.getDocument();

        this._defaultSettings = {}; // module default settings; updated when main model loaded from its model description
        this._settings = {};        // module current settings
        // this._data = {};            // per model data

        this._settingsObservers = [];  // external observers; eg. from application
        this._observers = [];          // internal scene api observers
        this._dependencies = [];
    }

    public static getClassType(): string {
        return AbstractModule._typeName;
    }

    protected static isForced(aObject: any, aProperty: any) {
        let descriptor = Object.getOwnPropertyDescriptor(aObject, aProperty);
        return !descriptor.writable;
    }

    protected static setForced(aObject: any, aProperty: any) {
        Object.defineProperty(aObject, aProperty, {
            writable: false
        });
    }

    /**
     * Supposes an object as an entry parameter
     * @param aSettings
     * @param aNewData
     * @param aChangedSettings
     * @param aPreviousSettings
     * @param aPath
     */
    protected static getDifference(aSettings: any, aNewData: any, aChangedSettings: any, aPreviousSettings: any, aForced: boolean = false, aPath: string = '') {

        for (let item in aNewData) {
            let path = aPath === '' ? item : aPath + '.' + item;
            // console.log(path);

            if (aNewData.hasOwnProperty(item)) { // for sure?

                if (aSettings.hasOwnProperty(item)) {

                    if (this.isForced(aSettings, item)) {
                        continue;
                    }

                    let value: any = aSettings[item];
                    let newValue: any = aNewData[item];

                    if (value === null || // Handle basic types
                        (typeof (value) === 'boolean' || value instanceof Boolean) ||
                        (typeof (value) === 'number' || value instanceof Number) ||
                        (typeof (value) === 'string' || value instanceof String)) {

                        if (value !== newValue) {
                            aPreviousSettings[item] = value;
                            aChangedSettings[item] = newValue;
                            aSettings[item] = newValue;
                        }

                        if (aForced) {
                            AbstractModule.setForced(aSettings, item);
                        }
                    }
                    else if (value instanceof Date) { // Handle Date
                        if (value.getTime() !== (newValue.getTime !== undefined ? newValue.getTime(): new Date(newValue))) {
                            aPreviousSettings[item] = new Date(value);
                            aChangedSettings[item] = new Date(newValue);
                            aSettings[item] = new Date(newValue);

                            if (aForced) {
                                AbstractModule.setForced(aSettings, item);
                            }
                        }
                    }
                    else if (value instanceof Array) { // Handle Array
                        if (!AbstractModule.equalArrays(value, newValue)) {
                            aPreviousSettings[item] = Settings.clone(value);
                            aChangedSettings[item] = Settings.clone(newValue);
                            aSettings[item] = Settings.clone(newValue);

                            if (aForced) {
                                AbstractModule.setForced(aSettings, item);
                            }
                        }
                    }
                    else if (value instanceof Float32Array) { // Handle Float32Array
                        if (!AbstractModule.equalArrays(value, newValue)) {
                            aPreviousSettings[item] = Settings.clone(value);
                            aChangedSettings[item] = Settings.clone(newValue);
                            aSettings[item] = Settings.clone(newValue);

                            if (aForced) {
                                AbstractModule.setForced(aSettings, item);
                            }
                        }
                    }
                    else if (value instanceof Object) { // Handle Object
                        aChangedSettings[item] = {};
                        aPreviousSettings[item] = {};
                        AbstractModule.getDifference(aSettings[item], aNewData[item], aChangedSettings[item], aPreviousSettings[item], aForced, aPath);
                    }
                    else {
                        // throw new Error("Unable to apply settings! Its type isn't supported." + aSettings);
                        Debug.logWarning("Unable to apply settings! Its type isn't supported." + aSettings);
                    }
                }
                else {
                    Debug.logWarning("Trying to set a wrong setting path: '" + path + "'");
                }

            }
            else {
                if (aNewData.hasOwnProperty(item)) {
                    Debug.logWarning("Trying to set a wrong setting path: '" + path + "'");
                }
            }
        }
    }

    /**
     * Compare 2 arrays of literals or two Float32Arrays.
     *
     * @param aArray1
     * @param aArray2
     *
     * @return boolean Returns true if arrays are equal false otherwise.
     */
    protected static equalArrays(aArray1: any[] | Float32Array, aArray2: any[] | Float32Array): boolean {
        // if the other array is a falsy value, return
        if (!aArray1 || !aArray2) {
            return false;
        }

        // compare lengths - can save a lot of time
        if (aArray1.length !== aArray2.length) {
            return false;
        }

        for (let i = 0, len = aArray1.length; i < len; i++) {
            // Check if we have nested arrays
            if ((aArray1[i] instanceof Array && aArray2[i] instanceof Array) || (aArray1[i] instanceof Float32Array && aArray2[i] instanceof Float32Array)) {
                // recurse into the nested arrays
                if (!AbstractModule.equalArrays(aArray1[i], aArray2[i])) {
                    return false;
                }
            }
            else if (aArray1[i] !== aArray2[i]) {
                // Warning - two different object instances will never be equal: {x:20} != {x:20}
                return false;
            }
        }
        return true;
    }

    public getType(): TSceneModuleType {
        return this._type;
    }

    // TODO Jarek: unregister above
    public abstract viewResized(aContentSize: vec2);
    public abstract viewChanged();

    public pushSettings() {
        Settings.push(this);
    }

    public popSettings() {
        // Settings.pop(this);
        this.set(Settings.pop(this));
    }

    public isSettingForced(aSettingsPath: string) {
        let parts = aSettingsPath.split('.');

        let parent: any = null;
        let property: any = this._settings;

        for (let i = 0; i < parts.length; i++) {
            if (property.hasOwnProperty(parts[i])) {
                parent = property;
                property = parent[parts[i]];
            }
        }

        if (!parent) {
            return false;
        }

        return AbstractModule.isForced(parent, parts[parts.length - 1]);
    }

    public initDependencies() {
        for (let dep of this._dependencies) {
            this.registerObserver(this.sceneAPI.getModuleByType(dep));
        }
    }

    public registerObserver(aObserver: AbstractModule) {
        // Test if object exists in array
        for (let i = 0; i < this._observers.length; i++) {
            if (this._observers[i] === aObserver) {
                return;
            }
        }

        this._observers.push(aObserver);
    }

    public unregisterObserver(aObserver: AbstractModule) {
        // Test if object exists in array
        let index = -1;
        for (let i = 0; i < this._observers.length; i++) {
            if (this._observers[i] === aObserver) {
                index = i;
                this._observers.splice(i, 1);
                return;
            }
        }
    }

    /**
     * Register external observer whose onChange method will be called module settings changes.
     *
     * Observer must have a method: onChange(aValues) where aValues is a object.
     *
     * @param aObserver Observer instance.
     */
    public registerSettingsObserver(aObserver: IExternalObserver) {
        // Test if object exists in array
        for (let i = 0; i < this._settingsObservers.length; i++) {
            if (this._settingsObservers[i].getName() === aObserver.getName()) {
                return;
            }
        }

        this._settingsObservers.push(aObserver);
    }

    public unregisterSettingsObserver(aObserver: IExternalObserver) {
        // Test if object exists in array
        let index = -1;
        for (let i = 0; i < this._settingsObservers.length; i++) {
            if (this._settingsObservers[i].getName() === aObserver.getName()) {
                index = i;
                this._settingsObservers.splice(i, 1);
                return;
            }
        }
    }

    /**
     * Notify observers with changes only.
     * @param aChanged
     */
    public notifySettingsObservers(aChanged: any) {
        for (let observer of this._settingsObservers) {

            // handle null imput
            let output: any = {};
            output[this.getType()] = aChanged || {};

            observer.onChange(output);
        }
    }

    public getModuleSettings(): any {
        return Settings.clone(this._settings);
    }

    public getSettings(): any {
        let output: any = {};
        output[this.getType()] = Settings.clone(this._settings);
        return output;
    }

    public getFullSettings(): any {
        let output: any = this.getSettings();

        for (let dep in this._dependencies) {
            let depModule = this.sceneAPI.getModule(this._dependencies[dep]);
            output[depModule.getType()] = Settings.clone(depModule.getFullSettings());
        }

        return output;
    }

    // public getSettings(): any {
    //     return Settings.clone(this._settings);
    // }
    //
    // public getFullSettings(): any {
    //     let output: any = {};
    //     output[this.getType()] = this.getSettings();
    //
    //     return output;
    // }

    // public setModule(aNewData: any) {
    //     if (aNewData) {
    //         if (aNewData.hasOwnProperty(this.getType())) {
    //             this.set(aNewData[this.getType()]);
    //         }
    //     }
    // }

    public force(aNewData: any) {
        // we need the old values here which are changed
        // let changed: any = {};
        // let previous: any = {};
        // AbstractModule.getDifference(this._settings, aNewData, changed, previous, true);

        this._set(aNewData, false, true);

        // // todo: tb this should be appliead first after all modules has their own coorest starting settings
        // if (!Settings.isLocked())
        // {
        //     // process settings - call api to pass new values to renderer, etc.
        //     if (Object.keys(changed).length !== 0 || Object.keys(previous).length !== 0) {
        //         this.applySettings(changed, previous);
        //
        //         // update application gui
        //         this.notifySettingsObservers();
        //     }
        // }
        //
        // if (!Settings.isLocked()) {
        //     // process dependencies
        //     this.notifyDependencies(changed, previous);
        // }
    }

    public set(aNewData: any, aForceAllChanged: boolean = false) {
        this._set(aNewData, aForceAllChanged);
    }

    // todo: bude to potreba ?????
    public get(): string {
        let s = this.getSettings();
        let settingsStr: string = Settings.serialize(s);
        return settingsStr;
    }

    // public updateDefaultSettings(aNewValues: any) {
    //     Settings.doSettings(this._type, this._defaultSettings, aNewValues);
    //     this.resetToDefaultSettings();
    // }

    public resetToDefaultSettings() {
        let previous: any = Settings.clone(this._settings);
        this._settings = Settings.clone(this._defaultSettings); // JArek:!!! this access the settings here in abstrat, but almost al; derived modules has its own _defaultSettings member so this will be always empty

        this.applySettings(this._settings, previous);

        this._notifySettingObservers();
    }

    public getData(aModelId?: string): any {
        let result = {};
        if (aModelId) {
            result[aModelId] = this.getModelData(aModelId);
        }
        else {
            for (let m in this._data) {
                result[m] = this.getModelData(m);
            }
        }

        return result;
    }

    /**
     *
     * Parse json from model description and call regular .set method with correct json object
     * This method is here to solve the model description content mix per document and mer model data
     * We need the per document aka application data to be decoupled form the model description.
     *
     * @param aJsonObject a part of JSON.parsed model description string so it is already "JSON" object.
     */
    public setFromModelDescription(aJsonObject: any) {
        //
    }

    /**
     * Set module settings to new values.
     * Only correct settings paths are processed and changed.
     *
     * @param aNewData New settings object.
     * @param aForceAllChanged Flag to notify all settings are changed and must be processed. Use for module initialization.
     * @param aForceSettings Force this settings to module. When forced, this settings is readonly afterwards.
     * @private
     */
    protected _set(aNewData: any, aForceAllChanged: boolean = false, aForceSettings: boolean = false) {

        // we need the old values here which are changed
        let changed: any = {};
        let previous: any = {};
        if (!aForceAllChanged) {
            AbstractModule.getDifference(this._settings, aNewData, changed, previous, aForceSettings);
        }
        else {
            changed = aNewData;
            previous = {}; // Settings.clone(this._defaultSettings);
        }

        // lock check
        if (!Settings.isLocked()) {
            // process settings - call api to pass new values to renderer, etc.
            if (Object.keys(changed).length !== 0 || Object.keys(previous).length !== 0) { // TB: || Object.keys(previous).length !== 0 not needed/not correct
                this.applySettings(changed, previous);

                // update application gui
                this.notifySettingsObservers(changed);
            }
        }

        if (!Settings.isLocked()) {
            // process dependencies
            this.notifyDependencies(changed, previous);
        }
    }

    // TB this will not work, because there are no refs to modules inside this._observers
    protected _notifyObservers(aObservers: any[], aMethod?: string, aArgs?: any[]) {
        for (let i = 0; i < aObservers.length; i++) {
            if (aObservers[i][aMethod] !== undefined) {
                aObservers[i][aMethod].apply(aObservers[i], aArgs);
            } else {
                Debug.logWarning("Callback method " + aMethod + " does not exists on observer " + typeof aObservers[i].getName());
            }
        }
    }

    protected _notifySettingObservers() {
        this._notifyObservers(this._settingsObservers, "onChange", this.getSettings());
    }

    /**
     * Take the updates from the json object and call appropriate methods in particular module
     * Runtime only
     *
     * @param aJsonObject
     */
    protected /*abstract*/ applySettings(aChangedSettings: any, aPrevisusSettings: any) {
        //
    }

    /**
     * Process changes of other modules
     *
     * @param aJsonObject
     */
    protected notifySettings(aChangedSettings: any, aPrevisusSettings: any) {
        //
    }

    protected getModelData(aModelId: string): any {
        let result = {};

        if (this._data.hasOwnProperty(aModelId)) {
            result[aModelId] = this.getModelData(aModelId);
        }

        return result;
    }

    protected notifyDependencies(aChangedSettings: any, aPreviousSettings: any) {
        let aNamedChangedSettings = {};
        aNamedChangedSettings[this.getType()] = aChangedSettings;

        let aNamedPreviousSettings = {};
        aNamedPreviousSettings[this.getType()] = aPreviousSettings;

        this._notifyObservers(this._observers, 'notifySettings', [aNamedChangedSettings, aNamedPreviousSettings]);
    }
}
